import {testIdProps} from '@github-ui/test-id-props'
import {ValidationErrorPopover} from '@github-ui/validation-error-popover'
import {flexRender} from '@tanstack/react-table'
import {clsx} from 'clsx'
import {type ComponentProps, type CSSProperties, memo, useLayoutEffect, useMemo, useRef} from 'react'

import {MemexColumnDataType, SystemColumnId} from '../../api/columns/contracts/memex-column'
import {ItemType} from '../../api/memex-items/item-type'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import {assertNever} from '../../helpers/assert-never'
import {columnActionable} from '../../helpers/column-actionable'
import {columnEditable} from '../../helpers/column-editable'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {getColumnMeta, getColumnModel} from '../../hooks/tanstack-table/helpers/column-api'
import {isAddColumnId, isDragDropColumnId} from '../../hooks/tanstack-table/helpers/column-ids'
import {getCellStylesFromColumn} from '../../hooks/tanstack-table/helpers/get-cell-styles'
import {rowIsSelected} from '../../hooks/tanstack-table/helpers/row-api'
import type {Cell, ColumnInstance, Row} from '../../hooks/tanstack-table/helpers/types'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {isValueBulkEditableColumn} from '../../models/column-capabilities'
import type {MemexItemModel} from '../../models/memex-item-model'
import {Resources} from '../../strings'
import {useDragToFill} from './bulk-fill-values'
import {useCellValidationMessage} from './cell-validation'
import {getCellFocusState} from './get-cell-focus-state'
import {useCopyPaste} from './hooks/use-copy-paste'
import {useTableNavigation} from './navigation'
import {useRowDragAllowed} from './row-reordering/reorderable-rows'
// eslint-disable-next-line primer-react/enforce-css-module-default-import
import classes from './table-cell.module.css'
import {
  type SelectionMethods,
  useAreCellsBulkSelected,
  useIsCellBulkSelected,
  useTableCellBulkSelectionActions,
  useTableCellBulkSelectionData,
} from './table-cell-bulk-selection'
import type {TableDataType} from './table-data-type'
import {cellTestId} from './test-identifiers'
import type {FocusCellElementType} from './use-item-data'
import {type ContentsProps, FocusCellContext, useTableFocusCell} from './use-table-focus-cell'

type Props = Pick<Cell<TableDataType>, 'row' | 'column'> & {
  focusCellElement: FocusCellElementType
  isDragging: boolean
  styles?: CSSProperties
  className?: string
  children?: React.ReactNode
}

function getTableCellAccessibleLabel(
  {row, column}: Pick<Cell<TableDataType>, 'row' | 'column'>,
  {hierarchyEnabled}: {hierarchyEnabled: boolean},
) {
  switch (column.columnDef.meta?.columnModel?.dataType) {
    case MemexColumnDataType.Title: {
      switch (row.original.contentType) {
        case ItemType.Issue:
        case ItemType.PullRequest:
        case ItemType.DraftIssue:
        case ItemType.AgentTask: {
          const title = row.original.getRawTitle()
          const status = row.original.getState()
          const parentIssue = row.original.getParentIssue()
          const isPR = row.original.contentType === ItemType.PullRequest

          const statusText = `status: ${status}${isPR ? ' pull request' : ''}`
          const subIssueDepth = row.subIssueData?.subIssueDepth
          if (hierarchyEnabled && parentIssue && subIssueDepth != null && subIssueDepth > 0) {
            // Issues directly under a top-level issue should be counted as "level 2", for no reason other than
            // that's what makes sense to us right now, since we think top-level issues should be "level 1" instead.
            return `${title}, ${statusText}. sub-issue of ${parentIssue.title}. Level ${subIssueDepth + 1}`
          }
          return `${title}, ${statusText}`
        }
        case ItemType.RedactedItem:
          return Resources.redactedItem
        default:
          // All content types should have been handled above, otherwise raise type-check error
          assertNever(row.original)
          // @ts-expect-error This is unreachable, but it fixes ESLint no-fallthrough analysis to have it in every branch
          return undefined
      }
    }
    default:
      return undefined
  }
}

export function TableCell({row, column, ...props}: Props) {
  const isSelected = rowIsSelected(row)

  const cell = useMemo(() => {
    return {column, row: {...row, isSelected}}
  }, [column, row, isSelected])
  const {
    state: {focus},
  } = useTableNavigation()
  const {isFocused, isEditing, isSuspended, isExpanded, replaceContents} = getCellFocusState(cell, focus)

  return (
    <InnerTableCell
      {...props}
      cell={cell}
      isFocused={isFocused}
      isEditing={isEditing}
      isSuspended={isSuspended}
      isExpanded={isExpanded}
      replaceContents={replaceContents}
    />
  )
}

const InnerTableCell = memo(function InnerTableCell({
  focusCellElement,
  isDragging,
  styles,
  children,
  cell,
  className: classNameProp,
  isFocused,
  isEditing,
  isExpanded,
  isSuspended,
  replaceContents,
}: Omit<Props, 'row' | 'column'> & {cell: Pick<Props, 'row' | 'column'>} & ReturnType<typeof getCellFocusState>) {
  const {isReadonly, hasWritePermissions} = ViewerPrivileges()
  const focusRef = useRef<HTMLElement>(null)

  const {bulkActions, isMaximumExceeded, dragToFill, isBulkSelected, tableHasBulkSelection} =
    useBulkActionsForTableCell(cell)

  const columnModel = getColumnModel(cell.column)
  const columnId = columnModel ? columnModel.id : cell.column.id
  const {validationMessage, validationMessageId} = useCellValidationMessage(cell.row.id, columnId)
  const contentOwner = cell.row.original.getOwner() || ''

  // Only allow dropping into edit mode if the column is editable for the current row
  const editable = useMemo(() => {
    if (cell.row.subIssueData?.missingSubIssues) return false
    if (cell.row.agentAssignmentTaskData) return false
    return (
      columnModel &&
      columnEditable(cell.row.original.contentType, contentOwner, columnModel.dataType, columnModel.isIssueField) &&
      hasWritePermissions
    )
  }, [
    cell.row.subIssueData?.missingSubIssues,
    cell.row.agentAssignmentTaskData,
    cell.row.original.contentType,
    columnModel,
    contentOwner,
    hasWritePermissions,
  ])

  const isDisabled = useMemo(() => {
    // For logged-out or read-only users,
    // the title of non-redacted items should not be disabled.
    if (isReadonly && cell.column.id === SystemColumnId.Title) {
      return cell.row.original.contentType === ItemType.RedactedItem
    }

    return !editable
  }, [cell.column.id, cell.row.original.contentType, editable, isReadonly])

  const isColumnBulkEditable = useMemo(
    () => columnModel && isValueBulkEditableColumn(columnModel.dataType),
    [columnModel],
  )

  const isActionable = useMemo(() => {
    return columnActionable(cell.row.original, columnModel?.dataType)
  }, [cell.row.original, columnModel?.dataType])

  const shouldShowBulkFillHandle = () =>
    isFocused && bulkActions !== null && isColumnBulkEditable && !isDisabled && !tableHasBulkSelection

  const {wrapperProps, contentsProps} = useTableFocusCell(
    cell,
    isFocused,
    isEditing,
    isExpanded,
    editable,
    isDisabled,
    replaceContents,
    focusRef,
  )
  const isInvalid = isFocused && isEditing && !!validationMessage
  const {isCellCopySource} = useCopyPaste()

  useLayoutEffect(() => {
    const cellElement = wrapperProps.ref.current // Only focus on the cell ref if we're not editing (since we focus the

    if (!cellElement || !isFocused || isEditing) return

    // cell value input elsewhere), we're not already active, or we don't
    // contain the active element (which would be the cell value input).
    if (!isEditing) {
      focusCellElement(cellElement, cell.column.id, {isSuspended})
    }
  }, [isEditing, wrapperProps.ref, focusCellElement, isSuspended, cell.column.id, isFocused])

  const canDragRow = useRowDragAllowed(cell.row)
  const isDragColumn = isDragDropColumnId(cell.column.id)
  const isDragDrop = isDragColumn && canDragRow

  const className = clsx(
    {
      hoverable: !isDragging,
      'is-focused': isFocused && !isDragDrop,
      'is-selected': isBulkSelected,
      'is-disabled-selected': isBulkSelected && (isDisabled || !isColumnBulkEditable || isMaximumExceeded),
      'is-copy-source': dragToFill?.isFillSourceCell || isCellCopySource(cell),
      'is-editing': isEditing,
      'is-invalid': isInvalid,
      draggable: isDragDrop,
      'is-dragging': isDragging,
      'cursor-not-allowed': isReadonly ? false : !isActionable && !editable,
      'is-first-row': cell.row.index === 0,
    },
    classNameProp,
  )

  const style = useMemo(
    () =>
      ({
        ...getCellStylesFromColumn(cell.column.id),
        // Use CSS variable for height to support different cell heights in table vs roadmap
        height: 'var(--table-cell-height)',
        flexGrow: isAddColumnId(cell.column.id) ? 1 : 'unset',
        overflowX: isDragDrop ? 'visible' : 'unset',
        whiteSpace: 'nowrap',
        ...styles,
      }) as CSSProperties,
    [cell.column.id, isDragDrop, styles],
  )

  const focusCellContextValue = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => ({
      focusCellElement: () => {
        if (wrapperProps.ref.current) {
          focusCellElement(wrapperProps.ref.current, cell.column.id, {
            isSuspended,
          })
        }
      },
      childRef: focusRef,
      wrapperRef: wrapperProps.ref,
    }),
    [focusCellElement, wrapperProps.ref, cell.column.id, isSuspended],
  )

  const {hierarchyEnabled} = useTableHierarchy()

  return (
    <FocusCellContext value={focusCellContextValue}>
      <StyledTableCell
        key={`cell_${cell.row.id}_${cell.column.id}`}
        role={columnModel?.dataType === MemexColumnDataType.Title ? 'rowheader' : 'gridcell'}
        className={className}
        aria-selected={isBulkSelected && !isDisabled}
        aria-label={getTableCellAccessibleLabel(cell, {hierarchyEnabled})}
        {...wrapperProps}
        style={style}
        {...testIdProps(cellTestId(cell.row.index, columnModel?.name || cell.column.id))}
        data-test-cell-is-focused={isFocused || undefined}
        data-test-cell-is-editing={isEditing || undefined}
        data-test-cell-is-suspended={isSuspended || undefined}
      >
        {flexRenderCell(cell, contentsProps)}
        {validationMessageId && <ValidationErrorPopover id={validationMessageId} message={validationMessage} />}
        {children}
        {dragToFill && (shouldShowBulkFillHandle() || dragToFill.isFillLastCell) && (
          <FillerHandle
            onMouseDown={dragToFill.onFillHandleMousedown}
            onDoubleClick={dragToFill.onFillHandleDoubleClick}
          />
        )}
      </StyledTableCell>
    </FocusCellContext>
  )
})

TableCell.displayName = 'TableCell'

type StyledTableCellProps = ComponentProps<'div'> & {ref?: React.Ref<HTMLDivElement>}

export const StyledTableCell = ({ref, className, ...props}: StyledTableCellProps) => (
  <div className={clsx(className, classes.StyledTableCell)} {...props} ref={ref} />
)
StyledTableCell.displayName = 'StyledTableCell'

const FillerHandle = memo(
  ({onMouseDown, onDoubleClick}: {onMouseDown: React.MouseEventHandler; onDoubleClick: React.MouseEventHandler}) => {
    const size = 12
    return (
      // We hide this from screen readers because there's just no good way to interact with this via the keyboard.
      // Instead we offer the Meta + D shortcut which does the same thing but requires selecting cells first.
      <div
        aria-hidden
        onMouseDown={onMouseDown}
        onDoubleClick={onDoubleClick}
        onClick={e => e.stopPropagation()}
        style={{
          height: `${size}px`,
          width: `${size}px`,
          bottom: `-${size / 2}px`,
          right: `-${size / 2}px`,
        }}
        className={classes.Box}
        {...testIdProps('table-cell-fill-handle')}
      />
    )
  },
)
FillerHandle.displayName = 'FillerHandle'

function flexRenderCell(cell: Pick<Cell<TableDataType>, 'row' | 'column'>, contentsProps: ContentsProps) {
  const {Cell: CellRenderer, CellEditor, columnModel} = getColumnMeta(cell.column) || {}
  const props = {
    row: cell.row,
    column: {id: cell.column.id, columnModel},
    ...contentsProps,
  }

  const {missingSubIssues = []} = props.row.subIssueData ?? {}
  if (missingSubIssues.length > 0 && props.column.id !== SystemColumnId.Title) {
    return null
  }

  if (props.isEditing && CellEditor) {
    return flexRender(CellEditor, props)
  }
  if (!props.isEditing && CellRenderer) {
    return flexRender(CellRenderer, props)
  }
  return null
}

function useBulkActionsForTableCell(cell: {row: Row<MemexItemModel>; column: ColumnInstance<MemexItemModel>}) {
  const {memex_mwl_table_cell_perf} = useEnabledFeatures()
  if (memex_mwl_table_cell_perf) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useBulkActionsForTableCellWithPerfEnabled()
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useBulkActionsForTableCellWithPerfDisabled(cell)
  }
}

function useBulkActionsForTableCellWithPerfDisabled(cell: {
  row: Row<MemexItemModel>
  column: ColumnInstance<MemexItemModel>
}) {
  const bulkActions = useTableCellBulkSelectionActions()
  const tableHasBulkSelection = useAreCellsBulkSelected()
  const {isMaximumExceeded} = useTableCellBulkSelectionData()
  const isBulkSelected = useIsCellBulkSelected(cell)
  const dragToFill = useDragToFill(cell)

  return {bulkActions, isMaximumExceeded, dragToFill, isBulkSelected, tableHasBulkSelection}
}

// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix
function useBulkActionsForTableCellWithPerfEnabled() {
  const bulkActions: SelectionMethods | null = null
  const tableHasBulkSelection = false
  const isMaximumExceeded = false
  const isBulkSelected = false
  const dragToFill: ReturnType<typeof useDragToFill> = null

  return {bulkActions, isMaximumExceeded, dragToFill, isBulkSelected, tableHasBulkSelection}
}
